import { z } from "zod";
import { cashAvailabilitySchema } from "./cash-plan-contract.js";

export const workbookSheetId = z.string().regex(/^(?:\d{3}|kredi-taksitleri|leasing|tahsilatlar|tedarikci-odemeleri|odeme-sozleri|butce-planlari|belge-vadeleri|manuel-plan)$/u);
const cents = z.string().regex(/^-?\d{1,28}$/u);
export const workbookCatalogSchema = z.object({
  company: z.string(), sheets: z.array(z.object({ id: workbookSheetId, name: z.string() })).max(208),
});
export const workbookSheetSchema = z.object({
  id: workbookSheetId, company: z.string(), date: z.iso.date(), observedAt: z.iso.datetime(),
  kind: z.enum(["balance", "instrument", "loan", "ledger", "schedule", "open", "plan"]),
  page: z.number().int().min(1), hasMore: z.boolean(), note: z.string(),
  rows: z.array(z.object({
    id: z.string(), code: z.string(), name: z.string(), document: z.string(),
    date: z.iso.date().nullable(), currency: z.string().min(1),
    amount: cents, paid: cents.nullable(), remaining: cents,
    direction: z.enum(["incoming", "outgoing", "none"]), status: z.string(),
  })).max(10000),
});
export const workbookContextSchema = z.object({
  rates: z.array(z.object({ currency: z.enum(["USD", "EUR"]), date: z.iso.date(),
    tlPerUnit: z.string().regex(/^\d+$/u) })).max(2),
  expenses: z.array(z.object({ code: z.string().regex(/^(720|730|740|760|770|780)$/u),
    month: z.string().regex(/^\d{4}-\d{2}$/u), amount: cents })).max(72),
  errors: z.array(z.enum(["rates", "expenses"])).max(2),
});
export type WorkbookContext = z.infer<typeof workbookContextSchema>;
export const workbookCoverageSchema = z.object({
  direction: z.enum(["payables", "receivables"]), origin: z.enum(["cash", "legacy", "empty"]),
  discovered: z.number().int().nonnegative(), read: z.number().int().nonnegative(),
  total: z.number().int().nonnegative().optional(),
  pending: z.number().int().nonnegative(), failed: z.number().int().nonnegative(),
  discoveryComplete: z.boolean(), complete: z.boolean(), oldestObservedAt: z.iso.datetime().nullable(),
  failures: z.array(z.object({ code: z.string(), name: z.string(), message: z.string(),
    lastAttemptAt: z.iso.datetime().nullable(), canRetry: z.boolean() })).max(10000),
});
export const workbookMetadataSchema = z.object({
  generatedAt: z.iso.datetime(), servedAt: z.iso.datetime(), cacheHit: z.boolean(),
  cacheExpiresAt: z.iso.datetime(), retryAfter: z.iso.datetime().nullable(),
  sources: z.array(z.object({ id: z.union([workbookSheetId, z.literal("rates")]),
    origin: z.enum(["erp", "saved", "user"]),
    status: z.enum(["ready", "partial", "failed", "deferred", "not_read"]),
    observedAt: z.iso.datetime().nullable(), recordCount: z.number().int().nonnegative().nullable(),
    message: z.string().nullable() })).max(209),
});
export const workbookDashboardSchema = z.object({
  context: workbookContextSchema.optional(),
  metadata: workbookMetadataSchema.optional(), coverage: z.array(workbookCoverageSchema).max(2).optional(),
  cashAvailability: z.array(cashAvailabilitySchema).max(100).optional(),
  planRevision: z.number().int().nonnegative().optional(),
  company: z.string(), date: z.iso.date(), catalog: workbookCatalogSchema.shape.sheets,
  // Explicit source reads can also retain a bounded ledger page from the catalog.
  sheets: z.array(workbookSheetSchema).max(208),
  errors: z.array(z.object({ id: workbookSheetId, message: z.string() })).max(208),
});
export type WorkbookSheet = z.infer<typeof workbookSheetSchema>;
export type WorkbookCatalog = z.infer<typeof workbookCatalogSchema>;
export type WorkbookDashboard = z.infer<typeof workbookDashboardSchema>;
export type WorkbookCoverage = z.infer<typeof workbookCoverageSchema>;
export type WorkbookMetadata = z.infer<typeof workbookMetadataSchema>;
